import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiPackage, FiSearch, FiTag, FiLayers } from "react-icons/fi";
import useProductVarietiesCache from "../../Components/Hooks/useProductVarietiesCache";

const AllVarieties = () => {
  const {
    varieties,
    loading,
    error,
    searchResults,
    search,
    refetch,
  } = useProductVarietiesCache();
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (query.trim().length > 0) {
      search(query.trim());
    }
  }, [query, search]);

  const displayVarieties = useMemo(() => {
    if (query.trim().length > 0) {
      return searchResults;
    }
    return varieties;
  }, [query, searchResults, varieties]);

  const getVarietyHref = (variety) => {
    const params = new URLSearchParams();

    const categoryId =
      variety?.productId?.subCategoryId?.categoryId?._id ||
      variety?.productId?.subCategoryId?.categoryId ||
      "";

    const subCategoryId =
      variety?.productId?.subCategoryId?._id || variety?.subCategoryId || "";

    const productId = variety?.productId?._id || variety?.productId || "";

    if (categoryId) params.set("category", categoryId);
    if (subCategoryId) params.set("subCategory", subCategoryId);
    if (productId) params.set("product", productId);
    if (variety?._id) params.set("variety", variety._id);

    return `/products${params.toString() ? `?${params.toString()}` : ""}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="2xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-semibold text-primaryColor">
            All Product Varieties
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-600">
            Search across every active product variety and jump straight to the product page with the correct category, sub-category, product, and variety selected.
          </p>
        </div>

        <div className="mb-8">
          <div className="relative max-w-3xl">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search all product varieties..."
              className="w-full rounded-full border border-gray-200 bg-white py-4 pl-12 pr-4 text-sm text-gray-800 shadow-sm outline-none transition focus:border-primaryColor/70 focus:ring-2 focus:ring-primaryColor/10"
            />
          </div>
        </div>

        {error && (
          <div className="rounded-3xl border border-dashed border-gray-300 bg-white/80 p-8 text-center">
            <p className="text-base font-semibold text-gray-700">Unable to load product varieties</p>
            <p className="mt-2 text-sm text-gray-500">Please refresh the page or try again later.</p>
            <button
              onClick={refetch}
              className="mt-5 inline-flex items-center rounded-full bg-primaryColor px-5 py-2.5 text-sm font-medium text-white transition hover:bg-primaryBtnHoverColor"
            >
              Retry
            </button>
          </div>
        )}

        {!error && loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="rounded-3xl border border-gray-200 bg-white p-3 shadow-sm animate-pulse">
                <div className="h-36 rounded-2xl bg-gray-200" />
                <div className="mt-3 h-3 w-2/3 rounded bg-gray-200" />
                <div className="mt-2 h-3 w-1/2 rounded bg-gray-200" />
                <div className="mt-4 h-9 rounded-xl bg-gray-100" />
              </div>
            ))}
          </div>
        )}

        {!error && !loading && displayVarieties.length === 0 && (
          <div className="rounded-3xl border border-dashed border-gray-300 bg-white/80 p-8 text-center">
            <p className="text-base font-semibold text-gray-700">
              {query.trim() ? "No varieties match your search" : "No product varieties found"}
            </p>
            <p className="mt-2 text-sm text-gray-500">
              {query.trim()
                ? "Try adjusting your search keywords."
                : "We will add new product varieties soon."}
            </p>
          </div>
        )}

        {!error && !loading && displayVarieties.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {displayVarieties.map((variety) => {
              const image = variety?.img;
              const productName = variety?.productId?.name || "Product";
              const categoryName =
                variety?.productId?.subCategoryId?.categoryId?.name ||
                variety?.productId?.subCategoryId?.name ||
                "Category";

              return (
                <Link
                  key={variety._id}
                  to={getVarietyHref(variety)}
                  className="group mx-auto w-full max-w-[20rem] rounded-[1.5rem] border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-primaryColor/30"
                >
                  <div className="relative overflow-hidden rounded-[1.25rem] bg-gradient-to-br from-[#FFF7F7] via-white to-[#FFEDEE]">
                    {image ? (
                      <img
                        src={image}
                        alt={variety?.varietyName || productName}
                        className="h-32 w-full object-cover transition duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-32 w-full items-center justify-center bg-gradient-to-br from-[#FFF7F7] to-[#FFEDEE]">
                        <div className="flex flex-col items-center justify-center rounded-full border border-white/70 bg-white/70 p-3 text-primaryColor shadow-sm">
                          <FiPackage className="text-2xl" />
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                    <div className="absolute left-3 top-3 rounded-full bg-white/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-primaryColor shadow-sm">
                      {categoryName}
                    </div>
                  </div>

                  <div className="p-3 sm:p-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1 rounded-full bg-primaryColor/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-primaryColor">
                        <FiTag className="text-[11px]" />
                        {variety?.grade || "Premium"}
                      </span>
                      <span className="text-[11px] font-medium text-gray-500">
                        {productName}
                      </span>
                    </div>

                    <h3 className="mt-2 text-base flex items-center justify-between gap-3 font-semibold text-gray-900 line-clamp-2">
                      <span className="flex-1">{variety?.varietyName || "Product Variety"}</span>
                      <div className="flex flex-wrap gap-2 text-[11px] text-gray-600">
                        <span className="rounded-full bg-gray-100 px-2 py-1">
                          MOQ {(variety?.slabAdjustedMOQ ?? variety?.minimum_MOQ) || 0} {variety?.standardizedUNIT || "unit"}
                        </span>
                      </div>
                    </h3>

                    <p className="text-primaryColor line-clamp-2 capitalize bg-primaryColor/10 mt-2 p-2 rounded-lg text-sm">
                      <span className="font-semibold">Description:</span> {variety?.description || "No description available."}
                    </p>

                    <div className="mt-4 flex items-center justify-between rounded-2xl border border-gray-100 bg-[#FFF9FA] px-3 py-2">
                      <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                        <FiLayers className="text-primaryColor" />
                        View
                      </span>
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primaryColor text-white transition duration-300 group-hover:translate-x-1">
                        <FiArrowRight />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AllVarieties;
